import React, { useMemo } from "react";
import { DateTime } from "luxon";
import { amountFormatter } from "services";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../shadcn/components/ui/table";
import { Badge } from "../../../../shadcn/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../shadcn/components/ui/card";
import { cn } from "../../../../shadcn/lib/utils";
import { Button } from "../../../../shadcn/components/ui/button";

interface BidHistoryTableProps {
  correctBids: any[];
  wrongBids: any[];
  status: string;
  setShowBidHistoryModel: any;
}

export default function BidHistoryTable({
  correctBids,
  wrongBids,
  status,
  setShowBidHistoryModel,
}: BidHistoryTableProps) {
  const allBids = useMemo(() => {
    if (!correctBids.length && !wrongBids.length) return [];

    return [...correctBids, ...wrongBids]
      .filter((bid) => bid.bid_timestamp)
      .map((bid) => ({
        ...bid,
        status: wrongBids.includes(bid) ? "Failed" : "Success",
      }))
      .sort((a, b) => {
        return b.bid_timestamp - a.bid_timestamp;
      });
  }, [correctBids, wrongBids]);

  // Group bids by IP address and check for multiple different emails (users)
  const ipGroups = useMemo(() => {
    const groups: { [key: string]: { bids: any[]; emails: Set<string> } } = {};
    allBids.forEach((bid) => {
      const ip = bid.user_ip || bid.meta?.user_ip || "unknown";
      const email = bid.meta?.email || bid.email || bid.user_email || "";

      if (!groups[ip]) {
        groups[ip] = { bids: [], emails: new Set() };
      }
      groups[ip].bids.push(bid);
      if (email) {
        groups[ip].emails.add(email);
      }
    });
    return groups;
  }, [allBids]);

  // Check if an IP has multiple different emails (different users)
  const hasMultipleEmailsOnSameIp = (ip: string) => {
    if (ip === "unknown") return false;
    const group = ipGroups[ip];
    return group && group.emails.size > 1;
  };

  const maskEmail = (email: string) => {
    if (!email) return "N/A";
    const [local, domain] = email.split("@");
    if (!local || !domain) return "N/A";

    // Mask local part: show middle character with asterisks before and after
    // Format: ***v**** (3+ asterisks, middle char, 4+ asterisks)
    let maskedLocal = "***";
    if (local.length > 2) {
      const middleIndex = Math.floor(local.length / 2);
      maskedLocal = `***${local[middleIndex]}***`;
    } else if (local.length > 0) {
      maskedLocal = "***";
    }

    // Mask domain part: show middle character of domain name, mask rest, then show TLD
    // Format: ***i***.com (3+ asterisks, middle char, 3+ asterisks, TLD)
    const [domainName, ...tldParts] = domain.split(".");
    const tld = tldParts.length > 0 ? `.${tldParts.join(".")}` : "";
    let maskedDomain = "***";
    if (domainName.length > 2) {
      const middleIndex = Math.floor(domainName.length / 2);
      maskedDomain = `***${domainName[middleIndex]}***${tld}`;
    } else if (domainName.length > 0) {
      maskedDomain = `***${tld}`;
    }

    return `${maskedLocal}@${maskedDomain}`;
  };

  const formatBidderName = (bid: any) => {
    const email = bid.meta?.email || bid.email;
    // Always show masked email during live auction
    if (status === "live") {
      return maskEmail(email);
    }
    // After auction ends, show masked email (or real email if needed)
    return email || "N/A";
  };

  const formatTime = (timestamp: number) => {
    return DateTime.fromMillis(
      typeof timestamp === "string" ? parseInt(timestamp) : timestamp,
    )
      .setZone("Asia/Kolkata")
      .toFormat("hh:mm:ss a");
  };

  const sameIpAlerts = useMemo(() => {
    const alerts: { ip: string; emails: string[] }[] = [];
    Object.keys(ipGroups).forEach((ip) => {
      const group = ipGroups[ip];
      if (group.emails.size > 1 && ip !== "unknown") {
        alerts.push({
          ip,
          emails: Array.from(group.emails),
        });
      }
    });
    return alerts;
  }, [ipGroups]);

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            Bid History
            {status === "live" && (
              <Badge
                variant="secondary"
                className="bg-blue-50 text-blue-600 text-xs px-2 py-0.5"
              >
                Bidders are placing their bids
              </Badge>
            )}
          </CardTitle>
          <Button
            // variant="outline"
            size="sm"
            className="text-xs px-2 py-1 h-6"
            onClick={() => setShowBidHistoryModel(true)}
          >
            View All
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {allBids.length > 0 ? (
          <>
            <div className="border rounded-lg overflow-hidden border-gray-200 mt-2">
              <div className="h-[200px] overflow-y-auto show-scrollbar">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs h-8 font-semibold uppercase tracking-wider px-3 py-2">
                        Bidder
                      </TableHead>
                      <TableHead className="text-xs h-8 font-semibold uppercase tracking-wider px-3 py-2">
                        Bid Amount
                      </TableHead>
                      <TableHead className="text-xs h-8 font-semibold uppercase tracking-wider px-3 py-2">
                        Status
                      </TableHead>
                      <TableHead className="text-xs h-8 font-semibold uppercase tracking-wider px-3 py-2">
                        Time (IST)
                      </TableHead>
                      <TableHead className="text-xs h-8 font-semibold uppercase tracking-wider px-3 py-2">
                        IP Address
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allBids.map((bid, index) => {
                      const ip = bid.user_ip || bid.meta?.user_ip || "unknown";
                      const isSameIp = hasMultipleEmailsOnSameIp(ip);
                      return (
                        <TableRow
                          key={bid.uuid || index}
                          className={cn(
                            "text-xs",
                            isSameIp && "bg-orange-50",
                            index % 2 === 0 && !isSameIp && "bg-white",
                            index % 2 === 1 && !isSameIp && "bg-gray-50",
                          )}
                        >
                          <TableCell className="font-medium px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              {formatBidderName(bid)}
                              {isSameIp && (
                                <Badge className="bg-orange-500 text-white text-[10px] px-1.5 py-0">
                                  Same IP
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell
                            className={cn(
                              "font-semibold px-3 py-2",
                              bid.status === "Failed"
                                ? "text-[#B60100]"
                                : "text-[#4DBB3E]",
                            )}
                          >
                            ₹{" "}
                            {amountFormatter(
                              bid.bidding_amount || bid.final_amount,
                            )}
                          </TableCell>
                          <TableCell
                            className={cn(
                              "font-semibold px-3 py-2",
                              bid.status === "Failed"
                                ? "text-[#B60100]"
                                : "text-[#4DBB3E]",
                            )}
                          >
                            {bid?.status === "Failed" ? "Failed" : "Accepted"}
                          </TableCell>
                          <TableCell className="px-3 py-2">
                            {formatTime(bid.frontend_timestamp)}
                          </TableCell>
                          <TableCell className="px-3 py-2">
                            <span className="font-mono text-xs text-gray-600">
                              {ip}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
            {sameIpAlerts.length > 0 && (
              <div className="mt-3 flex items-start gap-1.5 text-xs text-orange-700">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-1.5 flex-shrink-0" />
                <div>
                  Multiple bidders detected from IP{" "}
                  <strong>{sameIpAlerts[0].ip}</strong>. Banker may verify if
                  these bidders are related before confirming results.
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-6 text-gray-500 text-sm">
            No bid history available for this auction.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
